import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import axios from "axiosClient";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import moment from "moment";

const REQUIRED_HEADERS = [
  "smartupId",
  "createdAt",
  "enquiryId",
  "transactionId",
  "candidateName",
  "candidatePassport",
  "pinpp",
  "exam",
  "module",
  "examDate",
  "amount",
  "paymentMethod",
  "comments",
];

const normaliseHeaderCell = (value) =>
  value === undefined || value === null ? "" : `${value}`.trim();

const normaliseDotDate = (value) => {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "number") {
    const millis = Math.round((value - 25569) * 86400 * 1000);
    return moment.utc(millis).format("DD.MM.YYYY");
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format("DD.MM.YYYY");
  }

  const text = `${value}`.trim();
  if (!text) return "";

  const formats = [
    "DD.MM.YYYY",
    "D.M.YYYY",
    "DD/MM/YYYY",
    "D/M/YYYY",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
  ];

  const parsed = moment(text, formats, true);
  if (parsed.isValid()) return parsed.format("DD.MM.YYYY");
  return text;
};

const normaliseExamDate = (value) => normaliseDotDate(value);

const normaliseAmount = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const text = `${value}`.trim();
  if (!text) return "";
  const numeric = Number(text.replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(numeric)) return text;
  return numeric;
};

const hasAnyValue = (row) =>
  REQUIRED_HEADERS.some((key) => {
    const value = row?.[key];
    return value !== undefined && value !== null && `${value}`.trim() !== "";
  });

const PaymentsImportModal = ({ isOpen, toggle }) => {
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setFileName("");
      setParsing(false);
      setImporting(false);
      setRows([]);
      setSummary(null);
    }
  }, [isOpen]);

  const rowCountLabel = useMemo(() => {
    if (!rows.length) return "0";
    return `${rows.length}`;
  }, [rows.length]);

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    setSummary(null);

    if (!file) {
      setRows([]);
      setFileName("");
      return;
    }

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload a .xlsx file generated from the template.");
      setRows([]);
      setFileName(file.name);
      return;
    }

    setParsing(true);
    setRows([]);
    setFileName(file.name);

    try {
      const workbook = XLSX.read(await file.arrayBuffer(), {
        type: "array",
        cellDates: true,
      });

      const sheetName = workbook.SheetNames && workbook.SheetNames[0];
      const worksheet = sheetName ? workbook.Sheets[sheetName] : null;

      if (!worksheet) {
        throw new Error("No sheets found in workbook");
      }

      const headerMatrix = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        range: 0,
        blankrows: false,
      });

      const headerRow = (headerMatrix && headerMatrix[0]) || [];
      const normalisedHeaders = headerRow.map(normaliseHeaderCell);

      const isExactHeaderMatch =
        normalisedHeaders.length === REQUIRED_HEADERS.length &&
        REQUIRED_HEADERS.every(
          (header, index) => normalisedHeaders[index] === header,
        );

      if (!isExactHeaderMatch) {
        toast.error(
          "Template headers do not match. Please download the latest template and do not rename columns.",
        );
        setRows([]);
        return;
      }

      const dataRows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: true,
        blankrows: false,
      });

      const mappedRows = (dataRows || [])
        .map((row) => ({
          smartupId: row.smartupId,
          createdAt: normaliseDotDate(row.createdAt),
          enquiryId: row.enquiryId,
          transactionId: row.transactionId,
          candidateName: row.candidateName,
          candidatePassport: row.candidatePassport,
          pinpp: row.pinpp,
          exam: row.exam,
          module: row.module,
          examDate: normaliseExamDate(row.examDate),
          amount: normaliseAmount(row.amount),
          paymentMethod: row.paymentMethod,
          comments: row.comments,
        }))
        .filter(hasAnyValue);

      if (!mappedRows.length) {
        toast.warn("No data rows detected in the uploaded file.");
      }

      setRows(mappedRows);
    } catch (error) {
      console.error(error);
      toast.error(
        "Failed to parse the XLSX file. Please check the template format.",
      );
      setRows([]);
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!rows.length) {
      toast.warn("Please upload a filled template first.");
      return;
    }

    setImporting(true);
    setSummary(null);

    try {
      const response = await axios.post("/api/payments-import", {
        rows,
      });

      const responseSummary = response?.data?.summary || null;
      setSummary(responseSummary);

      if (response?.data?.success) {
        toast.success("Payments imported successfully.");
      } else {
        toast.warn("Import completed, but did not return success=true.");
      }
    } catch (error) {
      console.error(error);
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data ||
        error?.message ||
        "Server error";
      toast.error(`Import failed: ${msg}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Import Payments (XLSX)</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          Download the payment import template, fill it, then upload it here.
        </p>
        <FormGroup>
          <Label for="paymentsImportXlsx">Upload filled template (.xlsx)</Label>
          <Input
            id="paymentsImportXlsx"
            type="file"
            accept=".xlsx"
            disabled={importing}
            onChange={handleFileChange}
          />
        </FormGroup>

        {fileName && (
          <p className="text-muted">
            Selected file: <strong>{fileName}</strong>
          </p>
        )}

        {parsing && (
          <div className="d-flex align-items-center">
            <Spinner size="sm" className="mr-2" />
            <span>Parsing spreadsheet…</span>
          </div>
        )}

        {!parsing && rows.length > 0 && (
          <p className="mb-2">
            Parsed <strong>{rowCountLabel}</strong> row
            {rows.length === 1 ? "" : "s"}.
          </p>
        )}

        {summary && (
          <div className="mt-3">
            <p className="mb-1">
              Total rows: <strong>{summary.totalRows}</strong>
            </p>
            <p className="mb-1">
              Inserted: <strong>{summary.inserted}</strong>
            </p>
            <p className="mb-1">
              Upserted: <strong>{summary.upserted}</strong>
            </p>
            <p className="mb-1">
              Replaced: <strong>{summary.replaced || 0}</strong>
            </p>
            <p className="mb-0">
              Modified: <strong>{summary.modified}</strong>
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={importing}>
          Close
        </Button>
        <Button
          color="primary"
          onClick={handleImport}
          disabled={!rows.length || parsing || importing}
        >
          {importing ? <Spinner size="sm" /> : "Import"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PaymentsImportModal;
